import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CompareArrows as CompareArrowsIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  EventNote as EventNoteIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Sync as SyncIcon,
  NoteAlt as NoteAltIcon
} from '@mui/icons-material';
import api from '../services/api';

const AttendanceManagement = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner' || (user?.roles || []).includes('owner');
  const isLead = user?.role === 'lead' || (user?.roles || []).includes('lead');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    ['January','February','March','April','May','June','July','August','September','October','November','December'][new Date().getMonth()]
  );
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileLabel, setReconcileLabel] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [fullAttendanceData, setFullAttendanceData] = useState([]); // Store full records with IDs
  const [statistics, setStatistics] = useState(null);
  const [companyCalendar, setCompanyCalendar] = useState(null);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [applyDialog, setApplyDialog] = useState({ open: false, items: [], leaveType: 'LOP', loading: false });
  const [leaveTypeOptions, setLeaveTypeOptions] = useState(['LOP', 'EL', 'CL', 'SL', 'Unpaid Leave']);
  
  // Edit mode states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editedStatuses, setEditedStatuses] = useState([]);
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editHistory, setEditHistory] = useState(null);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [excludedFromReconciliation, setExcludedFromReconciliation] = useState({});

  // Attendance Validator tab state
  const [validatorFile, setValidatorFile] = useState(null);
  const [validatorLoading, setValidatorLoading] = useState(false);
  const [validatorError, setValidatorError] = useState('');
  const [validatorData, setValidatorData] = useState(null); // { monthYear, days, employees }
  const [validatorSaving, setValidatorSaving] = useState(false);
  const [validatorSaveResult, setValidatorSaveResult] = useState(null);

  // Attendance tab DB data state
  const _today = new Date();
  const [attFilterMonth, setAttFilterMonth] = useState(
    ['January','February','March','April','May','June','July','August','September','October','November','December'][_today.getMonth()]
  );
  const [attFilterYear, setAttFilterYear] = useState(_today.getFullYear().toString());
  const [attFromDay, setAttFromDay] = useState(1);
  const [attToDay, setAttToDay] = useState(_today.getDate());
  const [attDbData, setAttDbData] = useState(null);
  const [attDbLoading, setAttDbLoading] = useState(false);
  const [attDbError, setAttDbError] = useState('');
  const [attLateInThreshold, setAttLateInThreshold] = useState('09:30');
  const [attEarlyOutThreshold, setAttEarlyOutThreshold] = useState('18:30');
  const [attTimeFilter, setAttTimeFilter] = useState('all');
  const [attEmpSearch, setAttEmpSearch] = useState('');
  const [dailyReportOpen, setDailyReportOpen] = useState(false);
  const [dailyReportDay, setDailyReportDay] = useState(_today.getDate());
  const [dailyReportCopied, setDailyReportCopied] = useState(false);
  const [dailyReportText, setDailyReportText] = useState('');
  const [dailyReportLoading, setDailyReportLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResultOpen, setSyncResultOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState('');
  const [triggerDate, setTriggerDate] = useState(new Date().toISOString().split('T')[0]);

  // Day-note popover state
  const [noteAnchor, setNoteAnchor] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null); // { emp, day }
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // Attendance Excel export state
  const [exportingExcel, setExportingExcel] = useState(false);

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const years = ['2024', '2025', '2026'];
  const statusOptions = ['P', 'A', 'L', 'H', 'P/2', 'WO', 'HL', 'CO', 'NA', ''];

  const toHHMM = (totalHours) => {
    const h = Math.floor(totalHours);
    const m = Math.round((totalHours - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    if (!status) return '#f5f5f5';
    const s = status.toString().toUpperCase();
    if (s === 'P') return '#4CAF50';
    if (s === 'A') return '#F44336';
    if (s.includes('P/') || s === 'H') return '#2196F3';
    if (s === 'WO') return '#9E9E9E';
    if (s === 'L' || s === 'LV') return '#FFC107';
    if (s === 'HL') return '#FF6F00'; // Orange for company holidays
    if (s === 'CO') return '#9C27B0'; // Purple for Comp-Off
    if (s === 'NA') return '#E0E0E0'; // Light grey — Not Applicable (relieved employee)
    return '#f5f5f5';
  };

  const getStatusText = (status) => {
    if (!status) return '';
    return status.toString().toUpperCase();
  };

  // Calculate preview totals
  const calculatePreviewTotals = () => {
    if (!editedStatuses) return null;

    const counts = { present: 0, absent: 0, leave: 0, weekOff: 0, compOff: 0 };
    editedStatuses.forEach(status => {
      if (!status) return;
      const s = status.toString().toUpperCase();
      
      if (s === 'P') counts.present++;
      else if (s.includes('P/') || s === 'H') counts.present += 0.5;
      else if (s === 'CO') counts.compOff++; // Count Comp-Off separately, but it's also Present
      else if (s === 'A') counts.absent++;
      else if (s === 'L' || s === 'LV') counts.leave++;
      else if (s === 'WO' || s === 'HL') counts.weekOff++;
    });
    return counts;
  };

  // Handle edit employee
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEditedStatuses([...employee.dailyStatus]);
    setEditReason('');
    setEditDialogOpen(true);
  };

  // Handle save edits
  const handleSaveEdit = async () => {
    if (!editingEmployee) return;

    setSaving(true);
    setError('');

    try {
      const response = await api.put(`/attendance/${editingEmployee.id}/edit`, {
        dailyStatus: editedStatuses,
        reason: editReason
      });

      setSuccess(response.data.message || 'Attendance updated successfully!');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      setEditedStatuses([]);
      setEditReason('');
      fetchAttendanceData();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update attendance');
      console.error('Edit error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleExportAttendanceExcel = async () => {
    setExportingExcel(true);
    try {
      const monthYear = `${attFilterMonth}-${attFilterYear}`;
      const response = await api.get(`/attendance/${monthYear}/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Attendance_${monthYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export attendance Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  // Handle note icon click — open popover
  const handleNoteOpen = (event, emp, day) => {
    event.stopPropagation();
    const existingNote = (emp.dailyNotes || {})[String(day)] || '';
    setNoteTarget({ emp, day });
    setNoteDraft(existingNote);
    setNoteAnchor(event.currentTarget);
  };

  const handleNoteClose = () => {
    setNoteAnchor(null);
    setNoteTarget(null);
    setNoteDraft('');
  };

  const handleNoteSave = async () => {
    if (!noteTarget) return;
    setNoteSaving(true);
    try {
      await api.put(`/attendance/${noteTarget.emp.id}/day-note`, {
        day: noteTarget.day,
        note: noteDraft
      });
      // Update local calendar data so the indicator appears immediately
      setAttDbData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          calendar: prev.calendar.map(e => {
            if (e.id !== noteTarget.emp.id) return e;
            const updated = { ...(e.dailyNotes || {}) };
            if (noteDraft.trim()) updated[String(noteTarget.day)] = noteDraft.trim();
            else delete updated[String(noteTarget.day)];
            return { ...e, dailyNotes: updated };
          })
        };
      });
      handleNoteClose();
    } catch (err) {
      console.error('Failed to save note', err);
    } finally {
      setNoteSaving(false);
    }
  };

  // Handle view history
  const handleViewHistory = async (employee) => {
    try {
      const response = await api.get(`/attendance/${employee.id}/edit-history`);
      setEditHistory({ employee, ...response.data.data });
      setHistoryDialogOpen(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load edit history');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (validTypes.includes(file.type) || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a valid Excel file (.xls or .xlsx)');
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await api.post('/attendance/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const detectedMonth = response.data.data?.detectedMonth;
      const successMsg = response.data.message || 'Attendance uploaded successfully!';
      
      setSuccess(successMsg);
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      
      if (detectedMonth) {
        const [month, year] = detectedMonth.split('-');
        setSelectedMonth(month);
        setSelectedYear(year);
      }
      
      fetchAttendanceData();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to upload attendance file';
      const detectedMonth = error.response?.data?.detectedMonth;
      
      if (detectedMonth) {
        setError(`${errorMsg} (File contains: ${detectedMonth})`);
      } else {
        setError(errorMsg);
      }
      
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const fetchAttendanceData = async () => {
    setLoading(true);
    setError('');

    const monthYear = `${selectedMonth}-${selectedYear}`;

    try {
      // First, sync any approved Comp-Off leaves with attendance
      try {
        const syncResponse = await api.post(`/attendance/${monthYear}/sync-comp-offs`);
        if (syncResponse.data.data.compOffsMarked > 0) {
          console.log(`✅ Synced ${syncResponse.data.data.compOffsMarked} Comp-Off day(s) for ${syncResponse.data.data.employeesUpdated} employee(s)`);
        }
      } catch (syncError) {
        // Silently ignore sync errors (e.g., if no attendance data exists yet)
        console.log('Sync skipped:', syncError.response?.data?.message || 'No data to sync');
      }

      // Then fetch all the attendance data
      const [calendarResponse, statsResponse, fullDataResponse] = await Promise.all([
        api.get(`/attendance/${monthYear}/calendar`),
        api.get(`/attendance/statistics?monthYear=${monthYear}`),
        api.get(`/attendance/${monthYear}`)
      ]);

      setAttendanceData(calendarResponse.data.data.calendar || []);
      setFullAttendanceData(fullDataResponse.data.data.attendance || []);
      setStatistics(statsResponse.data.data);
      setCompanyCalendar(calendarResponse.data.data.companyCalendar);
    } catch (error) {
      if (error.response?.status === 404) {
        setAttendanceData([]);
        setFullAttendanceData([]);
        setStatistics(null);
        setError('No attendance data found for this month. Please upload attendance file.');
      } else {
        setError('Failed to fetch attendance data');
        console.error('Fetch error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReconcileLeaves = async (overrideMonthYear, overrideLabel) => {
    setReconciling(true);
    setError('');

    const monthYear = overrideMonthYear || `${selectedMonth}-${selectedYear}`;
    const label     = overrideLabel     || `${selectedMonth} ${selectedYear}`;
    setReconcileLabel(label);

    try {
      // Get list of excluded employee codes
      const excludedEmployees = Object.keys(excludedFromReconciliation).filter(
        code => excludedFromReconciliation[code]
      );

      const response = await api.get(`/attendance/${monthYear}/reconcile`);
      const reconcileData = response.data.data;

      // Filter out excluded employees from discrepancies
      if (excludedEmployees.length > 0) {
        reconcileData.discrepancies = reconcileData.discrepancies.filter(
          disc => !excludedEmployees.includes(disc.employeeCode)
        );
        // Update summary counts
        reconcileData.summary.employeesWithDiscrepancies = reconcileData.discrepancies.length;
        reconcileData.summary.excludedByCheckbox = excludedEmployees.length;
        
        // Calculate matched employees: Total - (Role/Dept Excluded + Checkbox Excluded + Discrepancies)
        const totalExcluded =
          (reconcileData.summary.excludedByRoleDept || 0) +
          excludedEmployees.length;
        
        reconcileData.summary.employeesMatched = 
          reconcileData.summary.totalEmployees - 
          totalExcluded -
          reconcileData.summary.employeesWithDiscrepancies;
      } else {
        reconcileData.summary.excludedByCheckbox = 0;
      }

      setReconciliationData(reconcileData);
      setReconcileDialogOpen(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reconcile leaves');
      console.error('Reconcile error:', error);
    } finally {
      setReconciling(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    setError('');
    setSuccess('');

    const monthYear = `${selectedMonth}-${selectedYear}`;

    try {
      const response = await api.delete(`/attendance/month/${monthYear}`);
      
      setSuccess(response.data.message || `Successfully deleted all data for ${monthYear}`);
      setDeleteConfirmOpen(false);
      
      setAttendanceData([]);
      setFullAttendanceData([]);
      setStatistics(null);
      fetchAttendanceData();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete attendance data');
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Handle generate salary Excel with validation
  const handleGenerateSalaryExcel = async () => {
    setGeneratingExcel(true);
    setError('');

    const monthYear = `${selectedMonth}-${selectedYear}`;

    try {
      // First, check leave reconciliation
      const reconcileResponse = await api.get(`/attendance/${monthYear}/reconcile`);
      const reconcileData = reconcileResponse.data.data;

      // Get list of excluded employee codes
      const excludedEmployees = Object.keys(excludedFromReconciliation).filter(
        code => excludedFromReconciliation[code]
      );

      // Filter out excluded employees from discrepancies
      const filteredDiscrepancies = reconcileData.discrepancies.filter(
        disc => !excludedEmployees.includes(disc.employeeCode)
      );

      // Check if there are any discrepancies (after filtering excluded employees)
      if (filteredDiscrepancies.length > 0) {
        setGeneratingExcel(false);
        setError(
          `⚠️ Leave Reconciliation Required!\n\n` +
          `Found ${filteredDiscrepancies.length} employee(s) with leave discrepancies.\n\n` +
          `Please resolve all leave reconciliation issues before generating the Final Salary Report:\n` +
          `• Click "Reconcile Leaves" button to view details\n` +
          `• Fix attendance records or approve missing leave requests\n` +
          `• Ensure all leaves match between attendance and approved requests\n` +
          `• Or use "Exclude Reconciliation" checkbox to skip specific employees\n\n` +
          `This ensures accurate salary calculations.`
        );
        return;
      }

      // If no discrepancies, proceed with Excel generation
      const response = await api.get(`/attendance/${monthYear}/salary-excel`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Report_${selectedMonth}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(
        `✅ Salary Excel generated successfully for ${monthYear}!\n\n` +
        `✓ All leave reconciliations verified\n` +
        `✓ ${reconcileData.summary.totalEmployees} employees processed\n` +
        `✓ File downloaded to your Downloads folder`
      );
    } catch (error) {
      if (error.response?.status === 404) {
        setError('No attendance data found for this month. Please upload attendance file first.');
      } else {
        setError(error.response?.data?.message || 'Failed to generate salary Excel');
      }
      console.error('Excel generation error:', error);
    } finally {
      setGeneratingExcel(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedMonth, selectedYear]);

  const getDaysInMonth = (month, year) => new Date(parseInt(year), ['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(month) + 1, 0).getDate();

  const handleDeleteRow = async (emp) => {
    if (!window.confirm(`Delete attendance record for ${emp.employeeName} (${emp.employeeCode}) for ${attFilterMonth}-${attFilterYear}? This cannot be undone.`)) return;
    try {
      await api.delete(`/attendance/record/${emp.id}`);
      fetchAttendanceDbData(attFilterMonth, attFilterYear);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete record');
    }
  };

  const fetchAttendanceDbData = async (month, year) => {
    setAttDbLoading(true);
    setAttDbError('');
    setAttDbData(null);
    try {
      const res = await api.get(`/attendance/${month}-${year}/calendar`);
      setAttDbData(res.data.data);
    } catch (e) {
      setAttDbError(e.response?.status === 404 ? `No attendance data found for ${month}-${year}` : (e.response?.data?.message || 'Failed to load.'));
    } finally {
      setAttDbLoading(false);
    }
  };

  useEffect(() => {
    const todayObj = new Date();
    const isCurrentMonth = attFilterMonth === ['January','February','March','April','May','June','July','August','September','October','November','December'][todayObj.getMonth()] && attFilterYear === todayObj.getFullYear().toString();
    setAttFromDay(1);
    setAttToDay(isCurrentMonth ? todayObj.getDate() : getDaysInMonth(attFilterMonth, attFilterYear));
    fetchAttendanceDbData(attFilterMonth, attFilterYear);
  }, [attFilterMonth, attFilterYear]);

  const previewTotals = editingEmployee ? calculatePreviewTotals() : null;

  const fetchDailyReport = async (day) => {
    setDailyReportLoading(true);
    setDailyReportText('');
    try {
      const monthYear = `${attFilterMonth}-${attFilterYear}`;
      const res = await api.get(`/attendance/${monthYear}/daily-report/${day}`);
      setDailyReportText(res.data.text || 'No data available.');
    } catch (e) {
      setDailyReportText('Failed to load report.');
    } finally {
      setDailyReportLoading(false);
    }
  };

  const handleCopyDailyReport = () => {
    navigator.clipboard.writeText(dailyReportText).then(() => {
      setDailyReportCopied(true);
      setTimeout(() => setDailyReportCopied(false), 2500);
    });
  };

  const openApplyDialog = (items) => {
    setApplyDialog({ open: true, items, leaveType: 'Unpaid Leave', loading: false });
  };

  const handleApplyReconcile = async () => {
    const { items, leaveType } = applyDialog;
    setApplyDialog(prev => ({ ...prev, loading: true }));
    try {
      const monthYear = `${attFilterMonth}-${attFilterYear}`;
      const payload = items.map(item => ({
        employeeCode: item.employeeCode,
        type: item.type,
        leaveType: item.type === 'leave' ? leaveType : undefined,
        days: item.type === 'leave' ? item.difference : item.compOffDifference
      }));
      const res = await api.post(`/attendance/${monthYear}/apply-reconcile`, { items: payload });
      setApplyDialog({ open: false, items: [], leaveType: 'LOP', loading: false });

      const failed = (res.data.results || []).filter(r => !r.success);
      if (res.data.applied > 0) {
        setSuccess(`Applied ${res.data.applied} of ${res.data.total} leave request(s) successfully.`);
      }
      if (failed.length > 0) {
        const details = failed.map(f => `${f.employeeCode}: ${f.error}`).join('\n');
        setError(`${failed.length} employee(s) could not be applied:\n${details}`);
      }

      // Re-run reconcile to refresh data
      await handleReconcileLeaves(`${attFilterMonth}-${attFilterYear}`, `${attFilterMonth} ${attFilterYear}`);
    } catch (err) {
      setApplyDialog(prev => ({ ...prev, loading: false }));
      setError(err.response?.data?.message || 'Failed to apply leave requests');
    }
  };

  const handleSyncETimeOffice = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/attendance/sync-etimeoffice', {
        month: attFilterMonth,
        year: attFilterYear,
        fromDay: attFromDay,
        toDay: attToDay
      });
      setSyncResult({ success: true, ...res.data });
      setSyncResultOpen(true);
      fetchAttendanceDbData(attFilterMonth, attFilterYear);
    } catch (err) {
      setSyncResult({ success: false, message: err.response?.data?.message || 'Sync failed' });
      setSyncResultOpen(true);
    } finally {
      setSyncing(false);
    }
  };

  const [reapplying, setReapplying] = useState(false);

  const handleTriggerDailySync = async () => {
    setTriggering(true);
    setTriggerMsg('');
    try {
      // Parse selected date into monthYear + day
      const d = new Date(triggerDate);
      const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const monthYear = `${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
      const day = d.getDate();
      const res = await api.post(`/attendance/apply-auto/${monthYear}/${day}`);
      setTriggerMsg(res.data.message || 'Applied successfully');
    } catch (err) {
      setTriggerMsg(err.response?.data?.message || 'Trigger failed');
    } finally {
      setTriggering(false);
      setTimeout(() => setTriggerMsg(''), 8000);
    }
  };

  const handleReapplyCalendar = async () => {
    const monthYear = `${attFilterMonth}-${attFilterYear}`;
    setReapplying(true);
    try {
      const res = await api.post(`/attendance/${monthYear}/reapply-calendar`);
      const { cellsChanged, employeesProcessed } = res.data.data;
      const msg = cellsChanged > 0
        ? `✅ Calendar applied: ${cellsChanged} cell(s) corrected across ${employeesProcessed} employee(s).`
        : `✅ No changes needed — attendance already matches the calendar.`;
      setSyncResult({ success: true, message: msg });
      setSyncResultOpen(true);
      fetchAttendanceDbData(attFilterMonth, attFilterYear);
    } catch (err) {
      setSyncResult({ success: false, message: err.response?.data?.message || 'Re-apply failed' });
      setSyncResultOpen(true);
    } finally {
      setReapplying(false);
    }
  };

  const handleValidatorParse = async () => {
    if (!validatorFile) return;
    setValidatorError('');
    setValidatorData(null);
    setValidatorSaveResult(null);
    setValidatorLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', validatorFile);
      const res = await api.post('/attendance/parse-preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setValidatorData(res.data);
    } catch (e) {
      setValidatorError(e.response?.data?.message || 'Failed to parse file.');
    } finally {
      setValidatorLoading(false);
    }
  };

  const handleSaveToDb = async () => {
    if (!validatorData) return;
    setValidatorSaving(true);
    setValidatorSaveResult(null);
    try {
      const res = await api.post('/attendance/save-performance', {
        monthYear: validatorData.monthYear,
        employees: validatorData.employees,
        fileName: validatorFile?.name || `monthperformance-${validatorData.monthYear}.xls`
      });
      setValidatorSaveResult({ success: true, ...res.data });
    } catch (e) {
      setValidatorSaveResult({ success: false, message: e.response?.data?.message || 'Failed to save.' });
    } finally {
      setValidatorSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        🗓️ Attendance Management
      </Typography>

      {/* ── Attendance ── */}
      <Box>
          {/* Filter bar */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select value={attFilterMonth} label="Month" onChange={e => setAttFilterMonth(e.target.value)}>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2} md={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select value={attFilterYear} label="Year" onChange={e => setAttFilterYear(e.target.value)}>
                    {['2024','2025','2026'].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2} md={1}>
                <TextField
                  label="From Day" type="number" size="small" fullWidth
                  value={attFromDay}
                  onChange={e => setAttFromDay(Math.max(1, Math.min(parseInt(e.target.value) || 1, attToDay)))}
                  inputProps={{ min: 1, max: attToDay }}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={1}>
                <TextField
                  label="To Day" type="number" size="small" fullWidth
                  value={attToDay}
                  onChange={e => setAttToDay(Math.max(attFromDay, Math.min(parseInt(e.target.value) || attFromDay, getDaysInMonth(attFilterMonth, attFilterYear))))}
                  inputProps={{ min: attFromDay, max: getDaysInMonth(attFilterMonth, attFilterYear) }}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={1}>
                <TextField
                  label="Late IN after" size="small" fullWidth
                  value={attLateInThreshold}
                  onChange={e => setAttLateInThreshold(e.target.value)}
                  placeholder="09:30"
                  inputProps={{ pattern: '[0-9]{2}:[0-9]{2}' }}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={1}>
                <TextField
                  label="Early OUT before" size="small" fullWidth
                  value={attEarlyOutThreshold}
                  onChange={e => setAttEarlyOutThreshold(e.target.value)}
                  placeholder="18:30"
                  inputProps={{ pattern: '[0-9]{2}:[0-9]{2}' }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter</InputLabel>
                  <Select value={attTimeFilter} label="Filter" onChange={e => setAttTimeFilter(e.target.value)}>
                    <MenuItem value="all">All Employees</MenuItem>
                    <MenuItem value="late-in">Late IN only</MenuItem>
                    <MenuItem value="early-out">Early OUT only</MenuItem>
                    <MenuItem value="both">Late IN + Early OUT</MenuItem>
                    <MenuItem value="absent">Absent only</MenuItem>
                    <MenuItem value="absent-late-in">Absent + Late IN</MenuItem>
                    <MenuItem value="absent-early-out">Absent + Late OUT</MenuItem>
                    <MenuItem value="absent-both">Absent + Late IN + Late OUT</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Search Employee" size="small" fullWidth
                  value={attEmpSearch}
                  onChange={e => setAttEmpSearch(e.target.value)}
                  placeholder="Name or code…"
                  inputProps={{ autoComplete: 'off' }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button variant="outlined" size="small" onClick={() => fetchAttendanceDbData(attFilterMonth, attFilterYear)} disabled={attDbLoading} startIcon={attDbLoading ? <CircularProgress size={16} /> : <RefreshIcon />}>
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip title="Export the attendance grid to Excel, including day notes" arrow placement="top">
                  <span>
                    <Button
                      variant="outlined" size="small" color="success"
                      startIcon={exportingExcel ? <CircularProgress size={16} /> : <DownloadIcon />}
                      disabled={!attDbData || exportingExcel}
                      onClick={handleExportAttendanceExcel}
                    >
                      {exportingExcel ? 'Exporting…' : 'Export Excel'}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Daily Report</Typography>
                      <Typography variant="body2">Generates a day-wise attendance summary for the selected "To Day".</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>Shows each employee's IN/OUT time, late arrivals, early departures, and absent status for that specific day. Useful for daily attendance review and follow-up.</Typography>
                    </Box>
                  }
                  arrow placement="top"
                >
                  <span>
                    <Button
                      variant="outlined" size="small" color="secondary"
                      startIcon={<EventNoteIcon />}
                      disabled={!attDbData}
                      onClick={() => { const d = attToDay; setDailyReportDay(d); setDailyReportOpen(true); fetchDailyReport(d); }}
                    >
                      Daily Report
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Sync eTime</Typography>
                      <Typography variant="body2">Pulls live punch data (IN/OUT times) directly from the eTime Office device for the selected month and day range.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>Use this to refresh attendance after employees have punched in/out. Company calendar WO overrides (swapped days) are applied automatically after sync.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#ffcc80' }}>Tip: Set "From Day" and "To Day" to sync only the days you need instead of the full month.</Typography>
                    </Box>
                  }
                  arrow placement="top"
                >
                  <Button
                    variant="contained" size="small" color="success"
                    startIcon={syncing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SyncIcon />}
                    disabled={syncing || isLead}
                    onClick={handleSyncETimeOffice}
                  >
                    {syncing ? 'Syncing…' : 'Sync eTime'}
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip title="Pick a date then click Run to apply auto-leave and comp-off for that day." arrow placement="top">
                  <span>
                    <TextField
                      type="date"
                      size="small"
                      value={triggerDate}
                      onChange={e => setTriggerDate(e.target.value)}
                      disabled={triggering || isLead}
                      InputLabelProps={{ shrink: true }}
                      label="Auto Apply Date"
                      sx={{ width: '100%' }}
                    />
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip title="Apply auto-leave and comp-off for the selected date." arrow placement="top">
                  <span>
                    <Button
                      variant="outlined" size="small" color="warning"
                      startIcon={triggering ? <CircularProgress size={16} /> : <SyncIcon />}
                      disabled={triggering || isLead}
                      onClick={handleTriggerDailySync}
                    >
                      {triggering ? 'Running…' : 'Run Daily Report Now'}
                    </Button>
                  </span>
                </Tooltip>
                {triggerMsg && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: triggerMsg.includes('fail') ? 'error.main' : 'success.main' }}>
                    {triggerMsg}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Apply Calendar</Typography>
                      <Typography variant="body2">Re-applies the company Leave Calendar rules to already-synced attendance data for the selected month.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>Use this when you have swapped a working day and a week-off (e.g., Sunday made working, Monday made WO). The eTime device marks days by fixed weekday schedule — this button corrects those cells to match your actual company calendar.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>Rules applied:
                        <br />• Calendar WO day + employee shows P/A → changed to WO
                        <br />• Calendar working day + employee shows WO → changed to A (absent)
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#ffcc80' }}>Note: Leaves (L), Holidays (HL), and Comp-Offs (CO) are never overridden.</Typography>
                    </Box>
                  }
                  arrow placement="top"
                >
                  <Button
                    variant="outlined" size="small" color="warning"
                    startIcon={reapplying ? <CircularProgress size={16} /> : <SyncIcon />}
                    disabled={reapplying || isLead}
                    onClick={handleReapplyCalendar}
                  >
                    {reapplying ? 'Applying…' : 'Apply Calendar'}
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Reconcile</Typography>
                      <Typography variant="body2">Cross-checks synced attendance data against approved Leave Requests and Comp-Off claims in the system for the selected month.</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>Highlights employees where:</Typography>
                      <Typography variant="body2">
                        • Attendance shows A/L but no approved leave found<br />
                        • Approved leave exists but attendance still shows P<br />
                        • CO marked in attendance but no approved Comp-Off<br />
                        • Approved Comp-Off not reflected in attendance
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#ffcc80' }}>Use before finalising salary to ensure leave and comp-off data is consistent.</Typography>
                    </Box>
                  }
                  arrow placement="top"
                >
                  <Button
                    variant="outlined" size="small" color="secondary"
                    startIcon={reconciling ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
                    disabled={reconciling}
                    onClick={() => handleReconcileLeaves(`${attFilterMonth}-${attFilterYear}`, `${attFilterMonth} ${attFilterYear}`)}
                  >
                    {reconciling ? 'Checking…' : 'Reconcile'}
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* DB data table */}
          {attDbError && <Alert severity="info" sx={{ mb: 2 }}>{attDbError}</Alert>}
          {attDbLoading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
          {attDbData && !attDbLoading && (() => {
            const monthIdx = ['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(attFilterMonth);
            const visibleDayNums = Array.from({ length: attToDay - attFromDay + 1 }, (_, i) => attFromDay + i);
            const hasPerf = attDbData.calendar.some(e => e.performanceDays);

            const toMins = str => {
              if (!str || str === '--:--') return null;
              const [h, m] = str.split(':').map(Number);
              return isNaN(h) || isNaN(m) ? null : h * 60 + m;
            };
            const lateInMins   = toMins(attLateInThreshold);
            const earlyOutMins = toMins(attEarlyOutThreshold);
            const isLateIn   = t => lateInMins   != null && toMins(t) != null && toMins(t) > lateInMins;
            const isEarlyOut = t => earlyOutMins != null && toMins(t) != null && toMins(t) < earlyOutMins;
            const getCellBg  = (inT, outT) => {
              const late = isLateIn(inT); const early = isEarlyOut(outT);
              if (late && early) return '#FFCDD2';
              if (late)          return '#FFF3E0';
              if (early)         return '#E3F2FD';
              return 'transparent';
            };

            const hasAbsentInRange = emp =>
              visibleDayNums.some(d => (emp.dailyStatus && emp.dailyStatus[d - 1] || '').toString().trim() === 'A');

            const empMatchesFilter = emp => {
              if (attTimeFilter === 'all') return true;
              const absent = hasAbsentInRange(emp);
              const hasLate  = emp.performanceDays && emp.performanceDays.some(pd => visibleDayNums.includes(pd.day) && isLateIn(pd.in));
              const hasEarly = emp.performanceDays && emp.performanceDays.some(pd => visibleDayNums.includes(pd.day) && isEarlyOut(pd.out));
              if (attTimeFilter === 'late-in')          return hasLate;
              if (attTimeFilter === 'early-out')        return hasEarly;
              if (attTimeFilter === 'both')             return hasLate && hasEarly;
              if (attTimeFilter === 'absent')           return absent;
              if (attTimeFilter === 'absent-late-in')   return absent || hasLate;
              if (attTimeFilter === 'absent-early-out') return absent || hasEarly;
              if (attTimeFilter === 'absent-both')      return absent || hasLate || hasEarly;
              return true;
            };
            const filteredCalendar = attDbData.calendar.filter(emp => {
              if (!empMatchesFilter(emp)) return false;
              if (!attEmpSearch.trim()) return true;
              const q = attEmpSearch.trim().toLowerCase();
              return emp.employeeName.toLowerCase().includes(q) || (emp.employeeCode || '').toLowerCase().includes(q);
            });

            return (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Attendance — {attFilterMonth} {attFilterYear}
                  </Typography>
                  <Chip label={`${filteredCalendar.length} / ${attDbData.calendar.length} employees`} size="small" />
                  <Chip label={`Day ${attFromDay}–${attToDay}`} size="small" variant="outlined" />
                  {hasPerf && <>
                    <Chip size="small" sx={{ bgcolor: '#FFF3E0', fontSize: '0.65rem' }} label={`Late IN > ${attLateInThreshold}`} />
                    <Chip size="small" sx={{ bgcolor: '#E3F2FD', fontSize: '0.65rem' }} label={`Early OUT < ${attEarlyOutThreshold}`} />
                    <Chip size="small" sx={{ bgcolor: '#FFCDD2', fontSize: '0.65rem' }} label="Both" />
                  </>}
                </Box>
                <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 150, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'grey.100' }}>Name</TableCell>
                        {visibleDayNums.map(d => {
                          const dayName = new Date(parseInt(attFilterYear), monthIdx, d).toLocaleDateString('en-US', { weekday: 'short' });
                          return (
                            <TableCell key={d} align="center" sx={{ fontWeight: 'bold', minWidth: hasPerf ? 52 : 40, px: 0.25, bgcolor: 'grey.100' }}>
                              <Box sx={{ fontSize: '0.75rem' }}>{d}</Box>
                              <Box sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{dayName}</Box>
                            </TableCell>
                          );
                        })}
                        <Tooltip title="Expected Work Days — working days in the period per company calendar (excludes WO; includes public holidays)" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e3f2fd', minWidth: 42, fontSize: '0.75rem' }}>EW</TableCell></Tooltip>
                        <Tooltip title="Present — days marked P by eTimeOffice (includes HL holidays and half-days)" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', minWidth: 40, fontSize: '0.75rem' }}>P</TableCell></Tooltip>
                        <Tooltip title="Absent — days marked A by eTimeOffice" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#ffebee', minWidth: 40, fontSize: '0.75rem' }}>A</TableCell></Tooltip>
                        <Tooltip title="Leave — approved leave requests (Unpaid, EL, CL, SL …) from the leave module" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fff9c4', minWidth: 40, fontSize: '0.75rem' }}>L</TableCell></Tooltip>
                        <Tooltip title="Claimed Comp-Off — days worked on WO/Sunday/holiday and approved as comp-off claims this month" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e0f2f1', minWidth: 40, fontSize: '0.75rem' }}>CC</TableCell></Tooltip>
                        <Tooltip title="Comp-Off taken — approved Comp Off leave requests from the leave module" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5', minWidth: 40, fontSize: '0.75rem' }}>CO</TableCell></Tooltip>
                        <Tooltip title="Week Off — days marked WO by eTimeOffice" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100', minWidth: 40, fontSize: '0.75rem' }}>WO</TableCell></Tooltip>
                        <Tooltip title="Expected Hours — P days × 9 hrs" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5', minWidth: 56, fontSize: '0.75rem' }}>Exp. Hrs</TableCell></Tooltip>
                        <Tooltip title="Total work hours including overtime, from eTimeOffice punch data" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e3f2fd', minWidth: 64, fontSize: '0.75rem' }}>Work+OT</TableCell></Tooltip>
                        <Tooltip title="Overtime hours only, from eTimeOffice punch data" arrow><TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fff8e1', minWidth: 56, fontSize: '0.75rem' }}>OT</TableCell></Tooltip>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCalendar.map(emp => (
                        <TableRow key={emp.employeeCode} hover>
                          <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, fontWeight: 500, fontSize: '0.78rem' }}>
                            {emp.employeeName}
                            <Box sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{emp.employeeCode}</Box>
                          </TableCell>
                          {visibleDayNums.map(d => {
                            const status = (emp.dailyStatus && emp.dailyStatus[d - 1]) ? emp.dailyStatus[d - 1].toString().trim() : '';
                            const perfDay = emp.performanceDays ? emp.performanceDays.find(pd => pd.day === d) : null;
                            const hasTime  = perfDay && perfDay.in && perfDay.in !== '--:--';
                            const noOutTime = hasTime && (!perfDay.out || perfDay.out === '--:--');
                            const cellBg   = noOutTime ? '#FFF8E1' : hasTime ? getCellBg(perfDay.in, perfDay.out) : 'transparent';
                            const lateIn   = hasTime && isLateIn(perfDay.in);
                            const earlyOut = hasTime && !noOutTime && isEarlyOut(perfDay.out);
                            const borderColor = noOutTime ? '#FF6F00' : (lateIn && earlyOut ? '#EF5350' : lateIn ? '#FB8C00' : '#1E88E5');
                            return (
                              <TableCell key={d} align="center" sx={{ p: 0.25, verticalAlign: 'top', bgcolor: cellBg, borderLeft: (hasTime || noOutTime) ? `2px solid ${borderColor}` : 'none' }}>
                                {status ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                      <Box sx={{
                                        bgcolor: getStatusColor(status),
                                        color: status === 'WO' ? 'text.primary' : 'white',
                                        borderRadius: 0.5,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        px: 0.5,
                                        py: 0.2,
                                        minWidth: 26,
                                        textAlign: 'center',
                                        lineHeight: 1.4
                                      }}>{status}</Box>
                                      {/* Note indicator dot */}
                                      {(emp.dailyNotes || {})[String(d)] && (
                                        <Box sx={{
                                          position: 'absolute', top: -3, right: -3,
                                          width: 6, height: 6, borderRadius: '50%',
                                          bgcolor: '#FF6F00', border: '1px solid white'
                                        }} />
                                      )}
                                    </Box>
                                    {hasTime && (
                                      <Box sx={{ fontSize: '0.58rem', lineHeight: 1.3, mt: 0.25, textAlign: 'center' }}>
                                        <Box sx={{ color: lateIn ? '#E65100' : 'text.secondary', fontWeight: lateIn ? 700 : 400 }}>{perfDay.in}</Box>
                                        <Box sx={{ color: noOutTime ? '#FF6F00' : earlyOut ? '#1565C0' : 'text.secondary', fontWeight: noOutTime || earlyOut ? 700 : 400 }}>{perfDay.out || '?'}</Box>
                                      </Box>
                                    )}
                                    {/* Note icon — always visible on hover via parent :hover, always shown if note exists */}
                                    <Tooltip title={(emp.dailyNotes || {})[String(d)] || 'Add note'} arrow disableHoverListener={!(emp.dailyNotes || {})[String(d)]}>
                                      <Box
                                        onClick={(e) => handleNoteOpen(e, emp, d)}
                                        sx={{
                                          mt: 0.25, cursor: 'pointer', opacity: (emp.dailyNotes || {})[String(d)] ? 1 : 0,
                                          '.MuiTableRow-root:hover &': { opacity: 1 },
                                          transition: 'opacity 0.15s',
                                          color: (emp.dailyNotes || {})[String(d)] ? '#FF6F00' : 'text.disabled',
                                          '&:hover': { color: '#E65100' },
                                          lineHeight: 0
                                        }}
                                      >
                                        <NoteAltIcon sx={{ fontSize: '0.7rem' }} />
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                ) : <Box sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>—</Box>}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#e3f2fd', fontWeight: 600 }}>{emp.expectedWorkDays ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#f1f8e9', fontWeight: 600 }}>{emp.totalP ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#fff5f5', fontWeight: 600 }}>{emp.totalA ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#fffde7', fontWeight: 600 }}>{emp.totalL ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#e0f2f1', fontWeight: 600 }}>{emp.totalCC ?? 0}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', color: '#9C27B0', fontWeight: 600 }}>{emp.totalCO ?? 0}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{emp.totalWO ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#f3e5f5', fontWeight: 600 }}>
                            {emp.totalP != null ? toHHMM(parseFloat(emp.totalP) * 9) : '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#e3f2fd' }}>{emp.totalWorkHours || '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#fff8e1' }}>{emp.overtimeHours || '—'}</TableCell>
                          {isOwner && (
                            <TableCell align="center" sx={{ p: 0.5 }}>
                              <Tooltip title={`Delete ${emp.employeeName}'s record`} arrow>
                                <IconButton size="small" color="error" onClick={() => handleDeleteRow(emp)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      {(() => {
                        const sumP   = filteredCalendar.reduce((s, e) => s + (parseFloat(e.totalP)  || 0), 0);
                        const sumCO  = filteredCalendar.reduce((s, e) => s + (parseFloat(e.totalCO) || 0), 0);
                        const sumA   = filteredCalendar.reduce((s, e) => s + (parseFloat(e.totalA)  || 0), 0);
                        const sumWO  = filteredCalendar.reduce((s, e) => s + (parseFloat(e.totalWO) || 0), 0);
                        const sumL   = filteredCalendar.reduce((s, e) => s + (parseFloat(e.totalL)  || 0), 0);
                        const sumCC  = filteredCalendar.reduce((s, e) => s + (parseFloat(e.totalCC) || 0), 0);
                        const parseHM = str => {
                          if (!str || str === '—') return 0;
                          const [h, m] = str.split(':').map(Number);
                          return (h || 0) * 60 + (m || 0);
                        };
                        const sumWorkMins = filteredCalendar.reduce((s, e) => s + parseHM(e.totalWorkHours), 0);
                        const sumOTMins   = filteredCalendar.reduce((s, e) => s + parseHM(e.overtimeHours),  0);
                        const minsToHM = m => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
                        const cellSx = { fontWeight: 800, fontSize: '0.75rem', borderTop: '2px solid #bbb' };
                        return (
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell colSpan={1} sx={{ ...cellSx, position: 'sticky', left: 0, bgcolor: 'grey.100', zIndex: 1 }}>
                              TOTAL ({filteredCalendar.length} emp)
                            </TableCell>
                            {visibleDayNums.map(d => (
                              <TableCell key={d} sx={{ borderTop: '2px solid #bbb' }} />
                            ))}
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#bbdefb' }}>{filteredCalendar.reduce((s, e) => s + (e.expectedWorkDays ?? 0), 0)}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#c8e6c9' }}>{sumP}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#ffcdd2' }}>{sumA}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#fff9c4' }}>{sumL}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#b2dfdb' }}>{sumCC}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, color: '#9C27B0' }}>{sumCO}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx }}>{sumWO}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#e1bee7' }}>{toHHMM(sumP * 9)}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#bbdefb' }}>{sumWorkMins ? minsToHM(sumWorkMins) : '—'}</TableCell>
                            <TableCell align="center" sx={{ ...cellSx, bgcolor: '#fff8e1' }}>{sumOTMins ? minsToHM(sumOTMins) : '—'}</TableCell>
                            {isOwner && <TableCell />}
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })()}

          {/* Upload / Parse section — hidden for now */}
          <Paper sx={{ p: 3, mb: 3, display: 'none' }}>
            <Typography variant="h6" gutterBottom>📂 Upload eTime Office File</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a <strong>monthperformance</strong> file exported from eTime Office. The file will be parsed locally — nothing is saved to the database.
            </Alert>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <input
                  accept=".xls,.xlsx"
                  style={{ display: 'none' }}
                  id="validator-file-input"
                  type="file"
                  onChange={(e) => {
                    setValidatorFile(e.target.files[0] || null);
                    setValidatorData(null);
                    setValidatorError('');
                  }}
                />
                <label htmlFor="validator-file-input">
                  <Button variant="outlined" component="span" fullWidth startIcon={<CloudUploadIcon />}>
                    {validatorFile ? validatorFile.name : 'Choose File'}
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleValidatorParse}
                  disabled={!validatorFile || validatorLoading}
                  startIcon={validatorLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                >
                  {validatorLoading ? 'Parsing...' : 'Parse File'}
                </Button>
              </Grid>
            </Grid>

            {validatorError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setValidatorError('')}>
                {validatorError}
              </Alert>
            )}
          </Paper>

          {validatorData && (() => {
            // Show only days up to today when the report is for the current month
            const today = new Date();
            const monthNames = ['January','February','March','April','May','June',
                                'July','August','September','October','November','December'];
            const [mName, mYear] = (validatorData.monthYear || '').split('-');
            const reportMonth = monthNames.indexOf(mName);
            const reportYear  = parseInt(mYear);
            const isCurrentMonth = reportMonth === today.getMonth() && reportYear === today.getFullYear();
            const cutoffDay = isCurrentMonth ? today.getDate() : 31;
            const visibleDays = validatorData.days.filter(d => d.day <= cutoffDay);

            return (
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6">
                    Parsed Attendance — {validatorData.monthYear}
                  </Typography>
                  <Chip label={`${validatorData.employees.length} employees`} size="small" />
                  <Chip
                    label={validatorData.fileType === 'performance' ? 'Performance (IN/OUT/WORK/OT)' : 'Summary (Status only)'}
                    size="small"
                    color={validatorData.fileType === 'performance' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={validatorSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveToDb}
                    disabled={validatorSaving || validatorData?.fileType !== 'performance'}
                  >
                    {validatorSaving ? 'Saving…' : 'Save to DB'}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<CompareArrowsIcon />}
                    disabled
                  >
                    Validate against JBDE OneHub
                  </Button>
                </Box>
              </Box>

              {validatorSaveResult && (
                <Alert
                  severity={validatorSaveResult.success ? 'success' : 'error'}
                  onClose={() => setValidatorSaveResult(null)}
                  sx={{ mb: 2 }}
                >
                  {validatorSaveResult.success
                    ? `${validatorSaveResult.message} — ${validatorSaveResult.inserted} inserted, ${validatorSaveResult.updated} updated`
                    : validatorSaveResult.message}
                  {validatorSaveResult.errors?.length > 0 && (
                    <Box sx={{ mt: 0.5, fontSize: '0.78rem' }}>
                      Errors: {validatorSaveResult.errors.join('; ')}
                    </Box>
                  )}
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 90, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'grey.100' }}>Emp Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 150, position: 'sticky', left: 90, zIndex: 3, bgcolor: 'grey.100' }}>Name</TableCell>
                      {visibleDays.map(d => (
                        <TableCell key={d.day} align="center" sx={{ fontWeight: 'bold', minWidth: validatorData.fileType === 'performance' ? 52 : 44, px: 0.25, bgcolor: 'grey.100' }}>
                          <Box sx={{ fontSize: '0.75rem' }}>{d.day}</Box>
                          <Box sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{d.name}</Box>
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', minWidth: 40, fontSize: '0.75rem' }}>P</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5', minWidth: 40, fontSize: '0.75rem' }}>CO</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#ffebee', minWidth: 40, fontSize: '0.75rem' }}>A</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100', minWidth: 40, fontSize: '0.75rem' }}>WO</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5', minWidth: 56, fontSize: '0.75rem' }}>Exp. Hrs</TableCell>
                      {validatorData.fileType === 'performance' && <>
                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e3f2fd', minWidth: 64, fontSize: '0.75rem' }}>Work+OT</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fff8e1', minWidth: 56, fontSize: '0.75rem' }}>OT</TableCell>
                      </>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validatorData.employees.map((emp) => (
                      <TableRow key={emp.employeeCode} hover>
                        <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, fontSize: '0.72rem' }}>
                          {emp.employeeCode}
                        </TableCell>
                        <TableCell sx={{ position: 'sticky', left: 90, bgcolor: 'background.paper', zIndex: 1, fontWeight: 500, fontSize: '0.78rem' }}>
                          {emp.employeeName}
                        </TableCell>

                        {validatorData.fileType === 'performance'
                          ? visibleDays.map(vd => {
                              const d = emp.days.find(ed => ed.day === vd.day) || { day: vd.day, status: '', in: '--:--', out: '--:--', work: '00:00', ot: '00:00' };
                              const hasTime = d.in && d.in !== '--:--';
                              return (
                                <TableCell key={d.day} align="center" sx={{ p: 0.25, verticalAlign: 'top' }}>
                                  {d.status ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                                      <Box sx={{
                                        bgcolor: getStatusColor(d.status),
                                        color: d.status === 'WO' ? 'text.primary' : 'white',
                                        borderRadius: 0.5,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        px: 0.5,
                                        py: 0.2,
                                        minWidth: 26,
                                        textAlign: 'center',
                                        lineHeight: 1.4
                                      }}>
                                        {d.status}
                                      </Box>
                                      {hasTime && (
                                        <Box sx={{ fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1.3, mt: 0.25, textAlign: 'center' }}>
                                          <Box>{d.in}</Box>
                                          <Box>{d.out}</Box>
                                        </Box>
                                      )}
                                    </Box>
                                  ) : <Box sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>—</Box>}
                                </TableCell>
                              );
                            })
                          : visibleDays.map(d => {
                              const status = emp.dailyStatus[d.day - 1] || '';
                              return (
                                <TableCell key={d.day} align="center" sx={{ p: 0.25 }}>
                                  {status ? (
                                    <Box sx={{
                                      bgcolor: getStatusColor(status),
                                      color: status === 'WO' ? 'text.primary' : 'white',
                                      borderRadius: 0.5,
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      py: 0.2,
                                      textAlign: 'center',
                                      minWidth: 26
                                    }}>
                                      {status}
                                    </Box>
                                  ) : '—'}
                                </TableCell>
                              );
                            })
                        }

                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 700, fontSize: '0.78rem' }}>{emp.daysPresent}</TableCell>
                        <TableCell align="center" sx={{ color: '#9C27B0', fontWeight: 700, fontSize: '0.78rem' }}>
                          {validatorData.fileType === 'performance'
                            ? (emp.days || []).filter(d => d.status === 'CO').length
                            : (emp.dailyStatus || []).filter(s => s === 'CO').length}
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'error.main', fontWeight: 700, fontSize: '0.78rem' }}>{emp.daysAbsent}</TableCell>
                        <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{emp.daysWeekOff}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.75rem', bgcolor: '#f3e5f5', fontWeight: 600 }}>
                          {emp.daysPresent != null ? toHHMM(parseFloat(emp.daysPresent) * 9) : '—'}
                        </TableCell>
                        {validatorData.fileType === 'performance' && <>
                          <TableCell align="center" sx={{ color: 'primary.main', fontSize: '0.75rem', fontWeight: 600 }}>{emp.totalWorkOT}</TableCell>
                          <TableCell align="center" sx={{ color: 'warning.dark', fontSize: '0.75rem' }}>{emp.totalOT}</TableCell>
                        </>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          );
          })()}
        </Box>

      {/* ── Dialogs (shared, outside tab panels) ── */}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen && editingEmployee !== null}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        {editingEmployee && (
          <>
            <DialogTitle>
              <Typography variant="h5">
                ✏️ Edit Attendance: {editingEmployee.employeeName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {editingEmployee.employeeCode} • {selectedMonth} {selectedYear}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={1}>
              {[...Array(31)].map((_, i) => {
                const dayNum = i + 1;
                const currentVal = editedStatuses[i] || '';
                const originalVal = editingEmployee.dailyStatus[i] || '';
                const isChanged = currentVal !== originalVal;
                
                return (
                  <Grid item xs={4} sm={3} md={2.4} key={i}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Day {dayNum}</InputLabel>
                      <Select
                        value={currentVal}
                        label={`Day ${dayNum}`}
                        onChange={(e) => {
                          const newStatuses = [...editedStatuses];
                          newStatuses[i] = e.target.value;
                          setEditedStatuses(newStatuses);
                        }}
                        MenuProps={{
                          disablePortal: true
                        }}
                        sx={{ bgcolor: isChanged ? '#fff3e0' : 'white' }}
                      >
                        {statusOptions.map(opt => (
                          <MenuItem key={opt} value={opt}>{opt || '(empty)'}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {isChanged && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', textAlign: 'center' }}>
                        Was: {originalVal || '(empty)'}
                      </Typography>
                    )}
                  </Grid>
                );
              })}
              </Grid>

              {previewTotals && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Preview Totals:</strong> Present: {previewTotals.present} | Comp-Off: {previewTotals.compOff} (counted as present) | Absent: {previewTotals.absent} | Leave: {previewTotals.leave} | Week Off: {previewTotals.weekOff}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Reason for Edit (Optional)"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                multiline
                rows={2}
                placeholder="E.g., Correcting data entry error, Late leave approval applied"
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button variant="outlined" onClick={() => setEditDialogOpen(false)} disabled={saving} startIcon={<CloseIcon />}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEdit} disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* History Dialog */}
      {historyDialogOpen && editHistory && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, p: 2 }}
          onClick={() => setHistoryDialogOpen(false)}>
          <Paper sx={{ p: 3, maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <Typography variant="h5" gutterBottom>
              📝 Edit History: {editHistory.employee.employeeName}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {editHistory.employee.employeeCode} • Total Edits: {editHistory.totalEdits}
            </Typography>

            {editHistory.history && editHistory.history.length > 0 ? (
              editHistory.history.map((session, idx) => (
                <Card key={idx} sx={{ my: 2, bgcolor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary">
                      📅 {new Date(session.editedAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      By: {session.editedBy}
                    </Typography>
                    {session.reason && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', my: 1 }}>
                        Reason: "{session.reason}"
                      </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                      {session.changes.map((change, cidx) => (
                        <Chip
                          key={cidx}
                          label={`Day ${change.day}: ${change.from || 'empty'} → ${change.to}`}
                          size="small"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Alert severity="info">No edit history found for this employee.</Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => setHistoryDialogOpen(false)}>Close</Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Reconciliation Dialog */}
      <Dialog open={reconcileDialogOpen && !!reconciliationData} onClose={() => setReconcileDialogOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { maxHeight: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0 }}>
          🔄 Attendance Reconciliation — {reconcileLabel}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {reconciliationData && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cross-check: Synced attendance vs Approved Leaves &amp; Comp-Offs in the system.
                {reconciliationData.summary.isCurrentMonth && (
                  <strong> Checked up to Day {reconciliationData.summary.dayCutoff} (today) — future days excluded.</strong>
                )}
              </Typography>

              {/* Summary cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Permanent Employees</Typography>
                      <Typography variant="h6">{reconciliationData.summary.totalEmployees}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {reconciliationData.summary.excludedByRoleDept > 0 && (
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ bgcolor: '#e3f2fd' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary">Excluded (Owner/OA/Driver)</Typography>
                        <Typography variant="h6" color="info.main">{reconciliationData.summary.excludedByRoleDept}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ bgcolor: reconciliationData.summary.employeesWithDiscrepancies > 0 ? '#ffebee' : '#e8f5e9' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Discrepancies</Typography>
                      <Typography variant="h6" color={reconciliationData.summary.employeesWithDiscrepancies > 0 ? 'error.main' : 'success.main'}>
                        {reconciliationData.summary.employeesWithDiscrepancies}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ bgcolor: '#e8f5e9' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">All Clear</Typography>
                      <Typography variant="h6" color="success.main">{reconciliationData.summary.employeesMatched}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Approved Leaves (Total)</Typography>
                      <Typography variant="h6">{reconciliationData.summary.totalApprovedLeaves} day(s)</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* All clear */}
              {reconciliationData.discrepancies.length === 0 && (
                <Alert severity="success" sx={{ my: 2 }}>
                  <strong>All Clear!</strong> Attendance data matches all approved leave &amp; comp-off records for {reconcileLabel}.
                </Alert>
              )}

              {/* Discrepancies */}
              {reconciliationData.discrepancies.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <WarningIcon color="error" fontSize="small" /> {reconciliationData.discrepancies.length} Employee(s) Need Attention
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      {reconciliationData.discrepancies.some(d => (d.difference ?? 0) > 0.05) && (
                        <Button size="small" variant="outlined" color="warning"
                          onClick={() => openApplyDialog(
                            reconciliationData.discrepancies
                              .filter(d => (d.difference ?? 0) > 0.05)
                              .map(d => ({ ...d, type: 'leave' }))
                          )}>
                          Apply All Leaves
                        </Button>
                      )}
                      {reconciliationData.discrepancies.some(d => (d.compOffDifference ?? 0) > 0.05) && (
                        <Button size="small" variant="outlined" color="success"
                          onClick={() => openApplyDialog(
                            reconciliationData.discrepancies
                              .filter(d => (d.compOffDifference ?? 0) > 0.05)
                              .map(d => ({ ...d, type: 'comp-off' }))
                          )}>
                          Apply All CO
                        </Button>
                      )}
                    </Box>
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, mb: 3 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 160 }}><strong>Employee</strong></TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}><strong>Att. L/A</strong></TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}><strong>Approved L</strong></TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}><strong>Leave Diff</strong></TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#e8f5e9' }}><strong>Att. CO</strong></TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#e8f5e9' }}><strong>Approved CO</strong></TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#e8f5e9' }}><strong>CO Diff</strong></TableCell>
                          <TableCell sx={{ minWidth: 200 }}><strong>Issue / Leave Details</strong></TableCell>
                          <TableCell align="center" sx={{ minWidth: 110 }}><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reconciliationData.discrepancies.map((disc, idx) => (
                          <TableRow key={idx} sx={{ bgcolor: idx % 2 === 0 ? 'inherit' : '#fafafa', verticalAlign: 'top' }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">{disc.employeeName}</Typography>
                              <Typography variant="caption" color="text.secondary">{disc.employeeCode}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#fff8f0' }}>
                              <Typography variant="body2">{disc.attendanceLeaves ?? '—'}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#fff8f0' }}>
                              <Typography variant="body2">{disc.approvedLeaves ?? '—'}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#fff8f0' }}>
                              {Math.abs(disc.difference ?? 0) > 0.05 ? (
                                <Chip label={disc.difference > 0 ? `+${disc.difference}` : disc.difference} size="small" color={disc.difference > 0 ? 'error' : 'warning'} />
                              ) : <Typography variant="body2" color="success.main">✓</Typography>}
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>
                              <Typography variant="body2">{disc.compOffDays ?? '—'}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>
                              <Typography variant="body2">{disc.approvedCompOffs ?? '—'}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ bgcolor: '#f1f8e9' }}>
                              {Math.abs(disc.compOffDifference ?? 0) > 0.05 ? (
                                <Chip label={disc.compOffDifference > 0 ? `+${disc.compOffDifference}` : disc.compOffDifference} size="small" color={disc.compOffDifference > 0 ? 'error' : 'warning'} />
                              ) : <Typography variant="body2" color="success.main">✓</Typography>}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>{disc.message}</Typography>
                              {/* +diff: show uncovered dates (absent in attendance, no approved leave) */}
                              {(disc.difference ?? 0) > 0.05 && disc.uncoveredDates?.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                  {disc.uncoveredDates.map((iso, i) => (
                                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                                      • {new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}: Absent (no leave request)
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                              {/* -diff: show approved leaves that aren't reflected in attendance */}
                              {(disc.difference ?? 0) < -0.05 && disc.leaveRequests?.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                  {disc.leaveRequests.map((lr, i) => (
                                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                                      • {lr.leaveType}: {new Date(lr.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                      {lr.startDate !== lr.endDate ? ` – ${new Date(lr.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` : ''} ({lr.numberOfDays}d)
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                {(disc.difference ?? 0) > 0.05 && (
                                  <Tooltip title={`Create approved leave request for ${disc.difference} day(s)`}>
                                    <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.68rem', py: 0.3, px: 1, minWidth: 90 }}
                                      onClick={() => openApplyDialog([{ ...disc, type: 'leave' }])}>
                                      Apply Leave
                                    </Button>
                                  </Tooltip>
                                )}
                                {(disc.compOffDifference ?? 0) > 0.05 && (
                                  <Tooltip title={`Create approved Comp-Off request for ${disc.compOffDifference} day(s)`}>
                                    <Button size="small" variant="outlined" color="success" sx={{ fontSize: '0.68rem', py: 0.3, px: 1, minWidth: 90 }}
                                      onClick={() => openApplyDialog([{ ...disc, type: 'comp-off' }])}>
                                      Apply CO
                                    </Button>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Matched employees */}
              {reconciliationData.matched?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ✅ {reconciliationData.matched.length} employee(s) fully matched:&nbsp;
                    {reconciliationData.matched.map(m => m.employeeName).join(', ')}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={() => setReconcileDialogOpen(false)}>Close</Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Reconcile Dialog */}
      <Dialog open={applyDialog.open} onClose={() => !applyDialog.loading && setApplyDialog(prev => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          {applyDialog.items.length === 1 && applyDialog.items[0]?.type === 'comp-off'
            ? `Apply Comp-Off — ${applyDialog.items[0]?.employeeName}`
            : applyDialog.items.length === 1
              ? `Apply Leave — ${applyDialog.items[0]?.employeeName}`
              : `Apply Leave / Comp-Off (${applyDialog.items.length} employees)`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create <strong>approved</strong> leave requests for the unaccounted attendance days.
            Dates are auto-detected from the first to last matching day in attendance.
          </Typography>
          <Box sx={{ mb: 2, maxHeight: 180, overflowY: 'auto' }}>
            {applyDialog.items.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                <Typography variant="body2">{item.employeeName} <Typography component="span" variant="caption" color="text.secondary">({item.employeeCode})</Typography></Typography>
                <Chip size="small"
                  label={item.type === 'comp-off' ? `CO: ${item.compOffDifference}d` : `Leave: ${item.difference}d`}
                  color={item.type === 'comp-off' ? 'success' : 'warning'} />
              </Box>
            ))}
          </Box>
          {applyDialog.items.some(i => i.type === 'leave') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">Leave Type:</Typography>
              <Chip label="Unpaid Leave" color="warning" size="small" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialog(prev => ({ ...prev, open: false }))} disabled={applyDialog.loading}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleApplyReconcile} disabled={applyDialog.loading}>
            {applyDialog.loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmOpen && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setDeleteConfirmOpen(false)}>
          <Paper sx={{ p: 3, maxWidth: 500, mx: 2 }} onClick={(e) => e.stopPropagation()}>
            <Typography variant="h6" gutterBottom color="error">⚠️ Confirm Delete</Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete ALL attendance data for <strong>{selectedMonth} {selectedYear}</strong>?
              <br /><br />
              This will permanently remove:
              <ul>
                <li>All {attendanceData.length} employee records</li>
                <li>All daily attendance data</li>
                <li>All statistics for this month</li>
              </ul>
              <strong>This action cannot be undone!</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
              <Button variant="contained" color="error" onClick={handleDeleteAll} disabled={deleting}
                startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}>
                {deleting ? 'Deleting...' : 'Yes, Delete All'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* eTime Office Sync Result Dialog */}
      <Dialog open={syncResultOpen} onClose={() => setSyncResultOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon color={syncResult?.success ? 'success' : 'error'} />
          eTime Office Sync
        </DialogTitle>
        <DialogContent dividers>
          {syncResult && (
            syncResult.success ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>{syncResult.message}</Alert>
                <Typography variant="body2">Total employees in eTime: <strong>{syncResult.data?.total ?? '—'}</strong></Typography>
                <Typography variant="body2">Synced: <strong>{syncResult.data?.synced ?? '—'}</strong></Typography>
                {syncResult.data?.unmatched?.length > 0 && (
                  <Box mt={1.5}>
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                      Unmatched ({syncResult.data.unmatched.length}) — no record in DB:
                    </Typography>
                    {syncResult.data.unmatched.map((u, i) => (
                      <Typography key={i} variant="body2" sx={{ ml: 1 }}>• {u.name} ({u.code})</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="error">{syncResult.message}</Alert>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSyncResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Daily Attendance Report Dialog */}
      <Dialog open={dailyReportOpen} onClose={() => setDailyReportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventNoteIcon color="primary" />
          Daily Attendance Report
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Report for Day:</Typography>
            <TextField
              type="number"
              size="small"
              value={dailyReportDay}
              onChange={e => {
                const d = Math.max(1, Math.min(parseInt(e.target.value) || 1, getDaysInMonth(attFilterMonth, attFilterYear)));
                setDailyReportDay(d);
                fetchDailyReport(d);
              }}
              inputProps={{ min: 1, max: getDaysInMonth(attFilterMonth, attFilterYear) }}
              sx={{ width: 80 }}
            />
            <Typography variant="body2" color="text.secondary">
              {attFilterMonth} {attFilterYear}
            </Typography>
          </Box>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              bgcolor: '#f9f9f9',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              minHeight: 180,
              lineHeight: 1.7
            }}
          >
            {dailyReportLoading ? 'Loading…' : (dailyReportText || 'No data available.')}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button
            variant="contained"
            startIcon={dailyReportCopied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
            color={dailyReportCopied ? 'success' : 'primary'}
            onClick={handleCopyDailyReport}
            disabled={dailyReportLoading || !dailyReportText}
          >
            {dailyReportCopied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
          <Button variant="outlined" onClick={() => setDailyReportOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Day-Note Popover */}
      <Popover
        open={Boolean(noteAnchor)}
        anchorEl={noteAnchor}
        onClose={handleNoteClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 1.5, width: 260 } }}
      >
        <Typography variant="caption" fontWeight={700} display="block" mb={0.5} color="text.secondary">
          Note — {noteTarget?.emp?.employeeName}, Day {noteTarget?.day}
        </Typography>
        <TextField
          autoFocus
          multiline
          rows={3}
          fullWidth
          size="small"
          placeholder="Add a note for this day..."
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          inputProps={{ maxLength: 200 }}
          sx={{ mb: 1 }}
        />
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button size="small" onClick={handleNoteClose}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleNoteSave} disabled={noteSaving}>
            {noteSaving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default AttendanceManagement;
